import React from 'react';
import { motion } from 'framer-motion';
import { Recycle, Share2, TrendingDown, LineChart } from 'lucide-react';
const pillars = [{
  icon: <Recycle className="w-6 h-6" data-id="element-675" />,
  label: '01',
  title: 'Made to come back',
  desc: 'Our packaging is built to survive hundreds of trips. When it comes back, we clean it up and send it out again. Nothing gets thrown away after a single use.',
  accent: 'leaf'
}, {
  icon: <Share2 className="w-6 h-6" data-id="element-676" />,
  label: '02',
  title: "Share, don't own",
  desc: 'Instead of buying your own fleet of packaging, you tap into a shared pool. You get the reach of a much bigger operation without paying to build one.',
  accent: 'sky'
}, {
  icon: <TrendingDown className="w-6 h-6" data-id="element-677" />,
  label: '03',
  title: 'Less to worry about',
  desc: 'You pay per trip and we handle everything else: the cleaning, the trucks, the tracking, the storage. Your team gets to focus on the product instead.',
  accent: 'accent'
}, {
  icon: <LineChart className="w-6 h-6" data-id="element-678" />,
  label: '04',
  title: 'Numbers you can stand behind',
  desc: 'We track every trip your packaging makes, so your sustainability reporting is built on what actually happened. Not estimates. Not guesswork.',
  accent: 'leaf'
}];
const accentMap: Record<string, {
  icon: string;
  border: string;
  num: string;
}> = {
  leaf: {
    icon: 'bg-mint text-leaf',
    border: 'border-leaf/15',
    num: 'text-leaf'
  },
  sky: {
    icon: 'bg-sky-soft text-sky',
    border: 'border-sky/15',
    num: 'text-sky'
  },
  accent: {
    icon: 'bg-accent/15 text-[#8a5d18]',
    border: 'border-accent/25',
    num: 'text-accent'
  }
};
export const WhyCycleBox: React.FC = () => {
  return <section className="py-32 bg-cream relative overflow-hidden" data-id="element-679">
      {/* Decorative circle */}
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full border border-leaf/10 pointer-events-none" data-id="element-680" />
      <div className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full border border-sky/10 pointer-events-none" data-id="element-681" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10" data-id="element-682">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-3xl mb-20" data-id="element-683">
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-primary mb-6 leading-[1.05]" data-id="element-684">
            We're not really in the packaging business.
          </h2>
          <p className="text-lg text-ink/70 leading-relaxed" data-id="element-685">
            We don't sell bottles, crates, or pallets. We operate the shared
            infrastructure that lets your supply chain run circular by default,
            saving capital, complexity, and carbon.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8" data-id="element-686">
          {pillars.map((pillar, i) => {
          const accent = accentMap[pillar.accent];
          return <motion.div key={i} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className={`p-8 md:p-10 rounded-2xl bg-white border ${accent.border} shadow-glossy hover:shadow-glossy-lg transition-all duration-300 hover:-translate-y-1 gloss-card relative`} data-id="element-687">
                <div className="relative" data-id="element-688">
                  <div className="flex items-start justify-between mb-8" data-id="element-689">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${accent.icon}`} data-id="element-690">
                      {pillar.icon}
                    </div>
                    <span className={`font-mono text-sm font-medium ${accent.num}`} data-id="element-691">
                      {pillar.label}
                    </span>
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl text-primary mb-4 leading-tight" data-id="element-692">
                    {pillar.title}
                  </h3>
                  <p className="text-ink/70 leading-relaxed" data-id="element-693">{pillar.desc}</p>
                </div>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
};